import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, Upload, Calendar, AlertCircle, User, Download } from 'lucide-react';
import { Driver } from '../../../types/saas';
import saasApiService from '../../../services/saasApi';

const schema = yup.object().shape({
  first_name: yup.string().required('Nombre requerido'),
  last_name: yup.string().required('Apellido requerido'),
  license_number: yup.string().required('Número de licencia requerido'),
  license_type: yup.string().oneOf(['A', 'B', 'C', 'D', 'E']).required('Tipo de licencia requerido'),
  license_expiry: yup.string().required('Fecha de vencimiento requerida'),
  phone: yup.string().optional(),
  email: yup.string().email('Email inválido').optional(),
  address: yup.string().optional(),
  hire_date: yup.string().optional(),
  salary: yup.number().min(0, 'Salario debe ser positivo').optional(),
  status: yup.string().oneOf(['active', 'inactive', 'suspended']).required('Estado requerido'),
  medical_certificate_expiry: yup.string().optional(),
  emergency_contact_name: yup.string().optional(),
  emergency_contact_phone: yup.string().optional(),
  notes: yup.string().optional(),
});

interface DriverFormProps {
  driver?: Driver | null;
  onClose: () => void;
  onSave: () => void;
}

const DriverForm: React.FC<DriverFormProps> = ({ driver, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [medicalFile, setMedicalFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      first_name: '',
      last_name: '',
      license_number: '',
      license_type: 'B',
      license_expiry: '',
      phone: '',
      email: '',
      address: '',
      hire_date: '',
      salary: '',
      status: 'active',
      medical_certificate_expiry: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      notes: ''
    }
  });

  useEffect(() => {
    if (driver) {
      reset({
        first_name: driver.first_name,
        last_name: driver.last_name,
        license_number: driver.license_number,
        license_type: driver.license_type,
        license_expiry: driver.license_expiry ? driver.license_expiry.split('T')[0] : '',
        phone: driver.phone || '',
        email: driver.email || '',
        address: driver.address || '',
        hire_date: driver.hire_date ? driver.hire_date.split('T')[0] : '',
        salary: driver.salary ? driver.salary.toString() : '',
        status: driver.status,
        medical_certificate_expiry: driver.medical_certificate_expiry ? driver.medical_certificate_expiry.split('T')[0] : '',
        emergency_contact_name: driver.emergency_contact_name || '',
        emergency_contact_phone: driver.emergency_contact_phone || '',
        notes: driver.notes || ''
      });
    }
  }, [driver, reset]);

  // Build absolute URL to backend static files (same logic as Vehicle/Trailer)
  const getFileUrl = (relativePath?: string) => {
    if (!relativePath) return '#';
    const apiBase = process.env.REACT_APP_SAAS_API_URL || 'http://localhost:5000/api';
    const backendBase = apiBase.replace(/\/?api\/?$/, '');
    const cleanPath = relativePath.replace(/^\//, '');
    return `${backendBase}/${cleanPath}`;
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError('');

      const formData = new FormData();
      
      // Agregar datos del formulario
      Object.keys(data).forEach(key => {
        if (data[key] !== '' && data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });

      // Agregar archivos si existen
      if (licenseFile) {
        formData.append('license_document', licenseFile);
      }
      if (medicalFile) {
        formData.append('medical_certificate', medicalFile);
      }

      if (driver) {
        await saasApiService.updateDriver(driver.id, formData);
      } else {
        await saasApiService.createDriver(formData);
      }

      onSave();
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al guardar el conductor');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'license' | 'medical') => {
    const file = event.target.files?.[0];
    if (file) {
      if (type === 'license') {
        setLicenseFile(file);
      } else {
        setMedicalFile(file);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {driver ? 'Editar Conductor' : 'Agregar Conductor'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Información personal */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Información Personal
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre *</label>
                <input
                  {...register('first_name')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Juan"
                />
                {errors.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Apellido *</label>
                <input
                  {...register('last_name')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Pérez"
                />
                {errors.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                <input
                  {...register('phone')}
                  type="tel"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+51 999 999 999"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  {...register('email')}
                  type="email"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="conductor@ejemplo.com"
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Dirección</label>
                <input
                  {...register('address')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Av. Principal 123, Lima"
                />
              </div>
            </div>
          </div>

          {/* Información de licencia */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Información de Licencia</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Número de Licencia *</label>
                <input
                  {...register('license_number')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="L12345678"
                />
                {errors.license_number && <p className="mt-1 text-sm text-red-600">{errors.license_number.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Tipo de Licencia *</label>
                <select
                  {...register('license_type')}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="A">Clase A - Motocicletas</option>
                  <option value="B">Clase B - Automóviles</option>
                  <option value="C">Clase C - Camiones</option>
                  <option value="D">Clase D - Transporte público</option>
                  <option value="E">Clase E - Articulados</option>
                </select>
                {errors.license_type && <p className="mt-1 text-sm text-red-600">{errors.license_type.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Vencimiento Licencia *
                </label>
                <input
                  {...register('license_expiry')}
                  type="date"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.license_expiry && <p className="mt-1 text-sm text-red-600">{errors.license_expiry.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Vencimiento Certificado Médico
                </label>
                <input
                  {...register('medical_certificate_expiry')}
                  type="date"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Información laboral */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Información Laboral</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Estado *</label>
                <select
                  {...register('status')}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                  <option value="suspended">Suspendido</option>
                </select>
                {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Fecha de Contratación</label>
                <input
                  {...register('hire_date')}
                  type="date"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Salario</label>
                <input
                  {...register('salary')}
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Contacto de emergencia */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Contacto de Emergencia</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre del Contacto</label>
                <input
                  {...register('emergency_contact_name')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="María Pérez"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Teléfono del Contacto</label>
                <input
                  {...register('emergency_contact_phone')}
                  type="tel"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+51 999 999 999"
                />
              </div>
            </div>
          </div>

          {/* Documentos */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Documentos</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Documento de Licencia</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                        <span>Subir archivo</span>
                        <input
                          type="file"
                          className="sr-only"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'license')}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">PDF, PNG, JPG hasta 5MB</p>
                    {licenseFile && (
                      <p className="text-sm text-green-600">{licenseFile.name}</p>
                    )}
                    {!licenseFile && (driver as any)?.license_document && (
                      <a
                        href={getFileUrl((driver as any).license_document)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <Download className="h-4 w-4 mr-1" /> Descargar Licencia
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Certificado Médico</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                        <span>Subir archivo</span>
                        <input
                          type="file"
                          className="sr-only"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'medical')}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">PDF, PNG, JPG hasta 5MB</p>
                    {medicalFile && (
                      <p className="text-sm text-green-600">{medicalFile.name}</p>
                    )}
                    {!medicalFile && (driver as any)?.medical_certificate && (
                      <a
                        href={getFileUrl((driver as any).medical_certificate)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <Download className="h-4 w-4 mr-1" /> Descargar Certificado Médico
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Notas</label>
            <textarea
              {...register('notes')}
              rows={3}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Información adicional sobre el conductor..."
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </div>
              ) : (
                driver ? 'Actualizar' : 'Crear'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DriverForm;
